import React from 'react';
import { SEARCH_DISTANCE_FILTERS_EXPERIMENT } from '../constants/experiments';
import type { GetStatsigEnrollmentResponse } from '../hooks/experiments';
import {
    GetEnrollmentStatus,
    StatsigEnrollmentType,
    useGetStatsigEnrollment,
} from '../hooks/experiments';
import { useEnvContext } from './env';

export const useDistanceFilterExperimentContext = () => {
    const value = React.useContext(DistanceFilterContext);

    if (value === undefined) {
        throw new Error(
            'useDistanceFilterExperimentContext must be used within ExperimentProvider',
        );
    }

    return value;
};

const INITIAL_DISTANCE_FILTER_VALUE = {
    status: GetEnrollmentStatus.DISABLED,
    variant: null,
};

//Casting here to prevent typescript from inferring that the
//value must match the DISABLED status response and instead
//maintain the dynamic GetStatsigEnrollmentResponse
export const DistanceFilterContext = React.createContext(
    INITIAL_DISTANCE_FILTER_VALUE as GetStatsigEnrollmentResponse,
);

/**
 * Experiment Provider that holds static information passed from
 * initialized statsig SDK to a provider that the discover app
 * can consume from.
 *
 * Each experiment should define its own individual context and
 * export its own hook. This is to prevent a given context from
 * becoming bloated and leveraged in too many places.
 *
 * eg:
 * ```typescript
 * return(
 *   <MyNextExperimentContext.Provider value={newExperimentValue}>
 *      <DistanceFilterContext.Provider value={distanceFilterValue}>
 *          {props.children}
 *      </DistanceFilterContext.Provider>
 *   </MyNextExperimentContext.Provider>
 * )
 * ```
 * @param props
 * @returns
 */
export const ExperimentProvider: React.FunctionComponent = (props) => {
    const { isBotRequest } = useEnvContext();

    const distanceFilterValue = useGetStatsigEnrollment({
        segmentIdentifier: SEARCH_DISTANCE_FILTERS_EXPERIMENT.layer,
        paramName: SEARCH_DISTANCE_FILTERS_EXPERIMENT.parameter,
        enrollmentType: StatsigEnrollmentType.LAYER,
        defaultValue: SEARCH_DISTANCE_FILTERS_EXPERIMENT.variants.CONTROL,
        enabled: !isBotRequest,
    });

    return (
        <DistanceFilterContext.Provider value={distanceFilterValue}>
            {props.children}
        </DistanceFilterContext.Provider>
    );
};

export default ExperimentProvider;
